import { useContext, type ReactElement } from 'react'
import moment from 'moment'
import { SchedulingWizardContext } from 'app/components/scheduling/SchedulingWizard'
import { normalizeProviderIds } from 'lib/preferredProviders'
import type { PreferredProvider } from 'lib/preferredProviders'

function PreferredLine({
  label,
  preferred,
  name,
  showBookability,
  bookable,
}: {
  label: string
  preferred?: PreferredProvider
  name?: string
  showBookability: boolean
  bookable: boolean
}): ReactElement {
  return (
    <div className="flex items-baseline gap-x-2 text-sm">
      <span className="w-32 shrink-0 text-subtle">{label}</span>
      {preferred ? (
        <span className="min-w-0 text-strong">
          <span className="font-medium">{name || `Unknown`}</span>
          <span className="text-minimal"> · last visit {moment(preferred.lastSeenDate).format(`MMM YYYY`)}</span>
          {showBookability && !bookable && <span className="text-orange-800"> · not bookable online</span>}
        </span>
      ) : (
        <span className="text-minimal">—</span>
      )}
    </div>
  )
}

/**
 * In-app-only panel that always surfaces the patient's preferred dentist AND
 * hygienist (from completed-appointment history), so the staff member booking
 * manually can see who the patient usually sees — even when that person has no
 * derived value or isn't available for online booking. The wizard separately
 * auto-selects whichever one matches the chosen appointment type when bookable.
 *
 * Rendered as a child of <SchedulingWizard> so it can read the wizard context.
 */
export function InAppPreferredProviderNotice({
  preferredProvider,
  preferredHygienist,
  providerNames,
}: {
  preferredProvider?: PreferredProvider
  preferredHygienist?: PreferredProvider
  providerNames: Map<string, string>
}): ReactElement | null {
  const { providers, currentStep } = useContext(SchedulingWizardContext)

  // Show from the appointment-type step (2) through review (4); hide on the
  // final confirmation. Bookability is only known once providers load (step 3+).
  if (currentStep < 2 || currentStep > 4) return null
  const showBookability = currentStep >= 3 && providers.length > 0

  const isBookable = (p?: PreferredProvider) =>
    !!p &&
    providers.some((pr) =>
      [pr.vendorId, pr.id].some((candidate) => normalizeProviderIds(candidate).includes(p.providerId)),
    )

  return (
    <div className="mx-6 mt-3 flex flex-col gap-1 rounded-md border border-primary-600/20 bg-primary-alpha-8 px-3 py-2.5">
      <div className="text-label-xs font-semibold uppercase tracking-wide text-primary-600">Preferred care team</div>
      <PreferredLine
        label="Preferred dentist"
        preferred={preferredProvider}
        name={preferredProvider ? providerNames.get(preferredProvider.providerId) : undefined}
        showBookability={showBookability}
        bookable={isBookable(preferredProvider)}
      />
      <PreferredLine
        label="Preferred hygienist"
        preferred={preferredHygienist}
        name={preferredHygienist ? providerNames.get(preferredHygienist.providerId) : undefined}
        showBookability={showBookability}
        bookable={isBookable(preferredHygienist)}
      />
    </div>
  )
}
